import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import {
  type Cart,
  type CartPairing,
  type CartProduct,
} from '@hungryroot/rest';
import { patchCartRequest } from '@hungryroot/rest/api/v2/customers/[id]/cart/patch';
import { type Location } from '@hungryroot/segment';
import { type RemoveIngredientMutation } from '../../types/RemoveIngredientMutation';
import { CartChangeType, executeRequestChangeHandlers } from '../useCartEvents';

/**
 * @deprecated PairingParams interface deprecated in favor of PairingMutationParams
 *
 * We cannot always use PairingMutationParams. One case is when we want to "duplicate"
 * a bundle. We do not have the original removed reason, so we have to use PairingParams
 * in that case
 */
interface PairingParams {
  pairing: {
    id: number;
    ingredients: number[];
  };
}

interface PairingMutationParams {
  pairing: {
    id: number;
    modifications: Array<{
      removed: number;
      reason_id: number;
    }>;
  };
}

interface ProductParams {
  product_id: number;
}

type Params = PairingParams | ProductParams | PairingMutationParams;

interface AddPairingMutationParams
  extends UseMutationOptions<
    Cart,
    unknown,
    {
      pairing: CartPairing;
      ingredients: CartProduct[];
      modifications?: RemoveIngredientMutation[];
      location?: Location;
    },
    { previousCart: Cart | null }
  > {}

export function useAddPairingSelf(params: AddPairingMutationParams) {
  return useMutation<
    Cart,
    unknown,
    {
      pairing: CartPairing;
      ingredients: CartProduct[];
      modifications?: RemoveIngredientMutation[];
      location?: Location;
    },
    { previousCart: Cart | null }
  >({
    mutationFn: async (params) => {
      executeRequestChangeHandlers({
        type: CartChangeType.RequestAddRecipe,
      });

      const { pairing, ingredients, modifications } = params;
      const { id: pairingId } = pairing;
      const patchParams: Params =
        modifications === undefined
          ? {
              pairing: {
                id: pairingId,
                ingredients: ingredients.map(({ id }) => id),
              },
            }
          : {
              pairing: {
                id: pairingId,
                modifications: modifications.map(
                  ({ ingredientId, reasonId }) => {
                    return {
                      removed: ingredientId,
                      reason_id: reasonId,
                    };
                  }
                ),
              },
            };
      const res = await patchCartRequest(patchParams);
      if (res.status !== 200) {
        throw res.data;
      }
      return res.data;
    },
    ...params,
  });
}
